import { ApplyReviewPayload, CardDto, DifficultyLevel, SubCard, SubcardReviewResult } from "../common/api";
import { reviewIntervals } from "../review/utils/reviewIntervals";

export type CarGrade = Extract<DifficultyLevel, 'good' | 'hard' | 'again'>;

/** At least this many graded subcards before a single mistake still rates the card Hard. */
export const HARD_MIN_GRADED = 3;

/**
 * The card's rating from the car answers, since there is no screen for
 * Again/Hard/Good/Easy: no mistakes → Good, one mistake out of 3+ → Hard,
 * otherwise Again. Unanswered items (undefined) are left out; undefined when
 * nothing was answered, so the card is not rated and stays due.
 */
export function gradeCarCard(results: (boolean | undefined)[]): CarGrade | undefined {
    const graded = results.filter((result): result is boolean => result !== undefined);
    if (graded.length === 0)
        return undefined;

    const mistakes = graded.filter(result => !result).length;
    if (mistakes === 0)
        return 'good';
    if (mistakes === 1 && graded.length >= HARD_MIN_GRADED)
        return 'hard';
    return 'again';
}

export interface CarItemAnswer {
    subcard?: SubCard;
    isCorrect?: boolean;
    timeSpent: number;
}

/** The review to apply for a rated card — the same payload Review sends. */
export function carReviewPayload(card: CardDto, grade: CarGrade, answers: CarItemAnswer[]): ApplyReviewPayload {
    const intervalMinutes = reviewIntervals(card)[grade];
    const subcardAnswers = answers.filter(answer => answer.subcard !== undefined);

    if (subcardAnswers.length === 0)
        return { cardId: card.cardId, intervalMinutes, difficulty: grade, source: 'car' };

    const subcardResults: SubcardReviewResult[] = subcardAnswers.map((answer, index) => ({
        subcardIndex: index,
        subcard: answer.subcard as SubCard,
        timeSpent: answer.timeSpent,
        isCorrect: answer.isCorrect,
    }));

    return {
        cardId: card.cardId,
        intervalMinutes,
        source: 'car',
        isMultiCard: true,
        multiCardResult: {
            cardId: card.cardId,
            totalTime: subcardAnswers.reduce((sum, answer) => sum + answer.timeSpent, 0),
            subcardResults,
            sessionResult: grade,
            completedAt: Date.now(),
        },
    };
}
